import { useState, useEffect, useCallback } from 'react';
import { Loader2, ClipboardCheck, CheckCircle2, Clock, ChevronDown, ChevronUp, Camera, PenTool, Gauge, Fuel, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ImageLightbox } from './ui/image-lightbox';
import { toast } from 'sonner';
import { useTenantSettings } from '../hooks/useTenantSettings';

/**
 * Desktop review panel for dry-hire inspections.
 * Lists pre + post inspections for a job, with expand-to-view-submitted
 * details (checklist, photos, readings, signature).
 */
export const HireInspectionsReviewPanel = ({ jobId, api }) => {
  const { fmtDateTime } = useTenantSettings();
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [expanded, setExpanded] = useState({});

  const fetchInspections = useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      const res = await api.get(`/hire-inspections?job_id=${jobId}`);
      setInspections(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load inspections');
    } finally {
      setLoading(false);
    }
  }, [jobId, api]);

  useEffect(() => {
    fetchInspections();
  }, [fetchInspections]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return <div className="mt-3 p-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 text-xs text-muted-foreground flex items-center gap-2">
      <Loader2 className="h-3 w-3 animate-spin" />Loading inspections…
    </div>;
  }

  if (inspections.length === 0) {
    return (
      <div className="mt-3 p-3 rounded-md border border-emerald-500/30 bg-emerald-500/5">
        <h4 className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4" />Inspections
        </h4>
        <p className="text-xs text-muted-foreground mt-1">No inspections yet. A pre-hire inspection auto-spawns when the job is created.</p>
      </div>
    );
  }

  return (
    <div className="mt-3 p-3 rounded-md border border-emerald-500/40 bg-emerald-500/5 space-y-2" data-testid="hire-inspections-review-panel">
      <h4 className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4" />Inspections
        <span className="text-[11px] font-normal text-muted-foreground">{inspections.length} total</span>
      </h4>
      <div className="space-y-1.5">
        {inspections.map(insp => (
          <InspectionRow
            key={insp.id}
            inspection={insp}
            expanded={!!expanded[insp.id]}
            onToggle={() => toggle(insp.id)}
            fmtDateTime={fmtDateTime}
            onImageClick={setLightboxUrl}
          />
        ))}
      </div>
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
};

const InspectionRow = ({ inspection, expanded, onToggle, fmtDateTime, onImageClick }) => {
  const isPre = inspection.inspection_type === 'pre_hire';
  const isSubmitted = inspection.status === 'submitted';
  const answered = (inspection.checklist_items || []).filter(i => i.status && i.status !== 'pending').length;
  const total = (inspection.checklist_items || []).length;
  const issueCount = (inspection.checklist_items || []).filter(i => i.status === 'issue').length;

  return (
    <div className="rounded border border-emerald-500/30 bg-background/40" data-testid={`review-insp-${inspection.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-emerald-500/5 rounded"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Badge className={`text-[10px] px-1.5 py-0 ${isPre ? 'bg-sky-500/20 text-sky-400 border-sky-500/40' : 'bg-amber-500/20 text-amber-400 border-amber-500/40'}`}>
            {isPre ? 'Pre-Hire' : 'Post-Hire'}
          </Badge>
          <span className={`text-[11px] ${isSubmitted ? 'text-emerald-400' : 'text-muted-foreground'}`}>
            {isSubmitted ? (
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Submitted</span>
            ) : (
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Draft {answered}/{total}</span>
            )}
          </span>
          {issueCount > 0 && (
            <Badge className="text-[10px] px-1 py-0 bg-red-500/20 text-red-400 border-red-500/40">
              {issueCount} issue{issueCount > 1 ? 's' : ''}
            </Badge>
          )}
          {isSubmitted && inspection.performed_by_name && (
            <span className="text-[10px] text-muted-foreground truncate">by {inspection.performed_by_name}</span>
          )}
        </div>
        {expanded ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-2 pb-2 pt-1 space-y-2 border-t border-emerald-500/20">
          {/* Readings */}
          {(inspection.hours_meter != null || inspection.fuel_level || inspection.odometer != null) && (
            <div className="flex flex-wrap gap-3 text-[11px]">
              {inspection.hours_meter != null && (
                <span className="flex items-center gap-1"><Gauge className="h-3 w-3 text-muted-foreground" />{inspection.hours_meter} hrs</span>
              )}
              {inspection.fuel_level && (
                <span className="flex items-center gap-1"><Fuel className="h-3 w-3 text-muted-foreground" />{inspection.fuel_level}</span>
              )}
              {inspection.odometer != null && (
                <span className="flex items-center gap-1">{inspection.odometer} km</span>
              )}
              {inspection.performed_at && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3 w-3" />{fmtDateTime(inspection.performed_at)}
                </span>
              )}
            </div>
          )}

          {/* Checklist */}
          <div className="space-y-1">
            {(inspection.checklist_items || []).map(item => (
              <div key={item.id} className="flex items-start gap-2 text-[11px]">
                <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${
                  item.status === 'ok' ? 'bg-emerald-500' :
                  item.status === 'issue' ? 'bg-red-500' :
                  item.status === 'na' ? 'bg-gray-500' : 'bg-gray-700'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="truncate">{item.label}</span>
                    <Badge className={`text-[9px] px-1 py-0 ${
                      item.status === 'ok' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                      item.status === 'issue' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                      item.status === 'na' ? 'bg-gray-500/10 text-gray-400 border-gray-500/30' :
                      'bg-gray-700/10 text-gray-500 border-gray-700/30'
                    }`}>
                      {(item.status || 'pending').toUpperCase()}
                    </Badge>
                  </div>
                  {item.notes && <div className="text-muted-foreground italic mt-0.5">{item.notes}</div>}
                  {item.photo_urls?.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {item.photo_urls.map((p, i) => (
                        <button key={i} type="button" onClick={() => onImageClick(p)} title="View photo" className="cursor-zoom-in">
                          <img src={p} alt="" className="w-8 h-8 object-cover rounded border border-emerald-500/30 hover:opacity-80" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Inspector notes */}
          {inspection.notes && (
            <div className="text-[11px] p-1.5 rounded bg-background/50 border border-emerald-500/20">
              <span className="text-muted-foreground">Notes: </span>{inspection.notes}
            </div>
          )}

          {/* General photos */}
          {inspection.photos?.length > 0 && (
            <div>
              <div className="text-[10px] text-muted-foreground flex items-center gap-1 mb-1"><Camera className="h-3 w-3" />General photos ({inspection.photos.length})</div>
              <div className="flex flex-wrap gap-1">
                {inspection.photos.map((p, i) => (
                  <button key={i} type="button" onClick={() => onImageClick(p)} className="cursor-zoom-in">
                    <img src={p} alt="" className="w-10 h-10 object-cover rounded border border-emerald-500/30 hover:opacity-80" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Customer sign-off */}
          {isSubmitted && inspection.signature_url && (
            <div>
              <div className="text-[10px] text-muted-foreground flex items-center gap-1 mb-1">
                <PenTool className="h-3 w-3" />Signed by {inspection.customer_name || '—'}
              </div>
              <button type="button" onClick={() => onImageClick(inspection.signature_url)} className="cursor-zoom-in">
                <img src={inspection.signature_url} alt="signature" className="h-12 bg-[#1a1a1a] rounded border border-emerald-500/30 hover:opacity-80" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HireInspectionsReviewPanel;
